import {
  Endorsement,
  Jacket,
  JacketPdf,
  JacketsPageData,
  PolicyFieldsModel,
  PolicyType,
  RateTypes,
  StandaloneEndorsement,
  StandaloneEndorsementFormDataType,
  VoidJacket,
  InventoryFormItemModel,
  CreateInventoryPolicyModel,
  CreateInventoryPolicyResult,
  ValidateVirtualPoliciesModel,
  ValidateVirtualPoliciesResult,
  ValidateSingleVirtualPolicyModel,
  CreateFastPoliciesModel,
} from 'features/jackets/types';
import {
  SearchInventoryPoliciesRequestType,
  SearchInventoryPoliciesResponseType,
  UpdateInventoryPolicyStatusRequestType,
  UpdateInventoryPolicyStatusResponseType,
} from 'features/orderManagement/types';
import { getApiHost, getAgencyApiHost } from './host-by-env';
import { handleError } from 'utilities/utilities';

export async function getJacketTypes(stateCode: string, accountNumber: number, token: string): Promise<PolicyType[]> {
  const url = `${getApiHost()}jacket/jacket-types/${accountNumber}/${stateCode}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function getJackets(fileId: string, token: string): Promise<Jacket[]> {
  const url = `${getApiHost()}jacket/jackets/${fileId}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function getJacketPdf(policyNumber: string, token: string): Promise<JacketPdf> {
  const url = `${getApiHost()}jacket/policy-document/${policyNumber}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function getEndorsements(
  stateCode: string,
  underwriterCode: string,
  token: string,
): Promise<Endorsement[]> {
  const url = `${getApiHost()}jacket/endorsement-types/${stateCode}/${underwriterCode}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function getEndorsementsByPolicyDate(
  stateCode: string,
  underwriterCode: string,
  effectiveDate: string,
  token: string,
): Promise<Endorsement[]> {
  let url = `${getApiHost()}jacket/endorsement-types/${stateCode}/${underwriterCode}`;

  if (effectiveDate) {
    url = url.concat(`?effectiveDate=${effectiveDate}`);
  }
  const response = await fetch(url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function getStandaloneEndorsements(fileId: string, token: string): Promise<StandaloneEndorsement[]> {
  const url = `${getApiHost()}jacket/standalone-endorsement/${fileId}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function postStandaloneEndorsements(
  standaloneEndorsementData: StandaloneEndorsementFormDataType,
  token: string,
): Promise<StandaloneEndorsement[]> {
  const url = `${getApiHost()}jacket/standalone-endorsement`;
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(standaloneEndorsementData),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function postJackets(jacketsPageData: JacketsPageData, token: string): Promise<Jacket[]> {
  const url = `${getApiHost()}jacket/jackets`;
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(jacketsPageData),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function putJackets(jacketsPageData: JacketsPageData, token: string): Promise<Jacket[]> {
  const url = `${getApiHost()}jacket/jackets`;
  const response = await fetch(url, {
    method: 'PUT',
    body: JSON.stringify(jacketsPageData),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function deleteStandaloneEndorsements(fileId: string, token: string): Promise<boolean> {
  const url = `${getApiHost()}jacket/standalone-endorsement/${fileId}`;
  const response = await fetch(url, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function deletePendingJacket(jacketId: string, token: string): Promise<boolean> {
  const url = `${getApiHost()}jacket/delete-jacket/${jacketId}`;
  const response = await fetch(url, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function voidJacket(policyNumber: string, token: string): Promise<VoidJacket> {
  const url = `${getApiHost()}jacket/void`;
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify({ policyNumber }),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function getJacketFields(
  formNumber?: string,
  accountNumber?: number,
  stateCode?: string,
  county?: string,
  token?: string,
): Promise<PolicyFieldsModel> {
  const url = `${getApiHost()}jacket/policy-fields/${formNumber}/${accountNumber}/${stateCode}/${county}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function getRateTypes(
  formNumber?: string,
  stateCode?: string,
  policyEffectiveDate?: string,
  isExtendedCoverage?: boolean,
  countyName?: string,
  accountNumber?: number,
  token?: string,
): Promise<RateTypes[]> {
  if (typeof isExtendedCoverage == 'undefined') {
    isExtendedCoverage = false;
  }
  const url = `${getApiHost()}jacket/rate-types/${formNumber}/${stateCode}/${policyEffectiveDate}/${isExtendedCoverage}/${countyName}/${accountNumber}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function getInventoryFormsApi(
  firmId: number,
  token: string,
  stateCode?: string,
): Promise<InventoryFormItemModel[]> {
  let url = `${getApiHost()}jacket/inventory/forms/${firmId}`;
  if (stateCode) {
    url = url.concat(`?stateCode=${stateCode}`);
  }
  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function createInventoryPolicies(
  payload: CreateInventoryPolicyModel[],
  token: string,
): Promise<CreateInventoryPolicyResult[]> {
  const url = `${getApiHost()}jacket/inventory/policy`;
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function validateSingleVirtualPolicyApi(
  payload: ValidateSingleVirtualPolicyModel,
  token: string,
): Promise<ValidateVirtualPoliciesResult> {
  const url = `${getApiHost()}jacket/inventory/validate-single-virtual-policy`;
  const validationModel: ValidateSingleVirtualPolicyModel = {
    itemNumber: payload.itemNumber,
    firmId: payload.firmId,
    policyNumber: `${payload.itemNumber}-${payload.policyNumber}`,
  };
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(validationModel),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function validateVirtualPoliciesApi(
  payload: ValidateVirtualPoliciesModel,
  token: string,
): Promise<ValidateVirtualPoliciesResult> {
  const url = `${getApiHost()}jacket/inventory/validate-virtual-policies`;
  const validationModel: ValidateVirtualPoliciesModel = {
    itemNumber: payload.itemNumber,
    firmId: payload.firmId,
    startPolicyNumber: `${payload.itemNumber}-${payload.startPolicyNumber}`,
    endPolicyNumber: `${payload.itemNumber}-${payload.endPolicyNumber}`,
    suffix: payload.suffix,
  };
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(validationModel),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function createFastPoliciesApi(
  payload: CreateFastPoliciesModel,
  token: string,
): Promise<ValidateVirtualPoliciesResult> {
  const url = `${getApiHost()}jacket/inventory/fast-policies`;
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function searchInventoryPoliciesApi(
  payload: SearchInventoryPoliciesRequestType,
  token: string,
): Promise<SearchInventoryPoliciesResponseType[]> {
  const params = new URLSearchParams();

  if (payload.policyNoStart) {
    params.append('policyNoStart', payload.policyNoStart);
  }
  if (payload.policyNoEnd) {
    params.append('policyNoEnd', payload.policyNoEnd);
  }
  if (payload.firmId) {
    params.append('firmId', String(payload.firmId));
  }
  if (payload.formNo) {
    params.append('formNo', payload.formNo);
  }
  if (payload.accountNo) {
    params.append('accountNo', String(payload.accountNo));
  }
  if (payload.policyStatus) {
    params.append('policyStatus', String(payload.policyStatus));
  }

  const query = params.size > 0 ? `?${params.toString()}` : '';
  const url = `${getAgencyApiHost()}jacket-service/v1/inventory/searchPolicies${query}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });

  const json = await response.json();
  handleError(json);
  return json.Result.Policies;
}

export async function updateInventoryPolicyStatusApi(
  payload: UpdateInventoryPolicyStatusRequestType,
  token: string,
): Promise<UpdateInventoryPolicyStatusResponseType> {
  const url = `${getApiHost()}jacket/inventory/update-policy-status`;
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}
